import { Grid } from "@mantine/core";
import { useForm, yupResolver } from "@mantine/form";
import { memo, useRef } from "react";
import {
  InputMaybe,
  Scalars,
  UpdateTimeClockEntryInput,
  useGetEmployeesStationNameManyQuery,
  useGetTimeClockEntryQuery,
  useUpdateTimeClockEntryMutation,
} from "src/generated/graphql";
import { useDisclosure } from "@mantine/hooks";
import { date, object } from "yup";
import TimeInput from "src/components/TimeInput";
import DatePicker from "src/components/DatePicker";
import {
  TimeDiffType,
  createDateTime,
  getDateDiff,
} from "src/utils/dateHelper";
import { errorNotification } from "src/utils/alerts";
import FormCard from "src/components/FormCard";
import { MESSAGES } from "src/utils/constants";
import { GetTimeClockEntry } from "src/api/graphql/queries/Clock";
import Select from "src/components/Select";
import { useAppContext } from "src/api/AppContext";

type IFormProps = Omit<UpdateTimeClockEntryInput, "station"> & {
  employee: string;
  startedTime: InputMaybe<Scalars["DateTime"]>;
  endedTime: InputMaybe<Scalars["DateTime"]>;
  station?: string;
};

export const TimeClockEntryFormSchema = object().shape({
  startedAt: date().required("Start Date is required"),
  endedAt: date().required("End Date is required"),
  startedTime: date().required("Start Time is required"),
  endedTime: date().required("End Time is required"),
});

interface IProps {
  clockId: string;
  setrefetchTableData: (bool: boolean) => void;
  refetchTableData: boolean;
  isAllowWrite: boolean;
}

const TimeClockDetail = ({
  clockId,
  setrefetchTableData,
  refetchTableData,
  isAllowWrite,
}: IProps) => {
  const { currentLocation } = useAppContext();
  const isRefetch = useRef(refetchTableData);
  const [isFormActive, { toggle: toggleFormActive }] = useDisclosure(true);
  const { data } = useGetTimeClockEntryQuery({
    variables: { id: clockId },
    fetchPolicy: "network-only",
    onCompleted: () => {
      resetFormState();
    },
  });

  const [updateTimeClockEntry, { loading }] = useUpdateTimeClockEntryMutation({
    onCompleted: () => {
      isRefetch.current = !isRefetch.current;
      setrefetchTableData(isRefetch.current);
      toggleFormActive();
      form.resetDirty();
    },
    refetchQueries: [{ query: GetTimeClockEntry, variables: { id: clockId } }],
  });

  const form = useForm<IFormProps>({
    initialValues: {
      employee: "",
      endedAt: "",
      startedAt: "",
      startedTime: "",
      endedTime: "",
      station: "",
    },
    validate: yupResolver(TimeClockEntryFormSchema),
  });

  const handleUpdate = async (values: IFormProps) => {
    let _start = createDateTime(values.startedAt, values.startedTime);
    let _end = createDateTime(values.endedAt, values.endedTime);
    let _diff = getDateDiff(_start, _end, TimeDiffType.seconds);

    if (_diff < 0) {
      return errorNotification({
        message: "Start Time must be less than End",
      });
    }
    try {
      await updateTimeClockEntry({
        variables: {
          data: {
            startedAt: _start,
            endedAt: _end,
            ...(values.station && { station: { id: values.station } }),
          },
          timeClockEntryId: clockId,
        },
      });
    } catch (e: any) {
      console.error("Error: ", e.response);
    }
  };

  const resetFormState = () => {
    const { startedAt, endedAt, employee, station } =
      data?.timeClockEntryOne || {};
    let _data = {
      employee: employee?.id || "",
      startedAt: new Date(startedAt),
      endedAt: endedAt ? new Date(endedAt) : new Date(),
      station: station?.id,
      startedTime: new Date(startedAt),
      endedTime: endedAt ? new Date(endedAt) : new Date(),
    };
    form.setValues(_data);
    form.resetDirty(_data);
  };

  const handleEdit = () => {
    toggleFormActive();
  };

  const handleCancel = () => {
    resetFormState();
    toggleFormActive();
  };

  const _empId = form.getInputProps("employee").value;
  const _stationId = form.getInputProps("station").value;

  const { data: stations } = useGetEmployeesStationNameManyQuery({
    variables: {
      employeeId: _empId,
      locationId: currentLocation?.id || "",
    },
    skip: !_empId || !currentLocation,
    fetchPolicy: "network-only",
    onCompleted(data) {
      if (!data.employeeStationMany && !_stationId) return;
      // reset station if no matching station found
      if (!data.employeeStationMany?.find((s) => s.id === _stationId)) {
        form.setFieldValue("station", "");
      }
    },
  });

  const isFormUpdated = !isFormActive && form.isDirty() && form.isTouched();
  return (
    <FormCard
      title={"Basic Info"}
      loading={loading}
      bannerContent={isFormUpdated ? MESSAGES.UNSAVED_CHANGES : ""}
      onCancel={handleCancel}
      onEdit={isAllowWrite ? handleEdit : undefined}
      onSave={form.onSubmit((values) => handleUpdate(values))}
      isFormActive={isFormActive}
      overflow="visible"
    >
      {!!currentLocation?.stationsEnabled && (
        <Select
          data={
            stations?.employeeStationMany?.map((val) => ({
              value: val.id,
              label: val.name,
            })) || []
          }
          disabled={isFormActive}
          label="Select Station"
          {...form.getInputProps("station")}
        />
      )}
      <Grid justify="space-between">
        <Grid.Col span={6}>
          <DatePicker
            label="Start Date"
            maw={400}
            mx="auto"
            {...form.getInputProps("startedAt")}
            disabled={isFormActive}
          />
        </Grid.Col>
        <Grid.Col span={6}>
          <TimeInput
            // withSeconds
            label="Start Time"
            format="12"
            {...form.getInputProps("startedTime")}
            disabled={isFormActive}
          />
        </Grid.Col>
      </Grid>

      <Grid justify="space-between">
        <Grid.Col span={6}>
          <DatePicker
            label="End Date"
            maw={400}
            mx="auto"
            {...form.getInputProps("endedAt")}
            disabled={isFormActive}
          />
        </Grid.Col>
        <Grid.Col span={6}>
          <TimeInput
            // withSeconds
            label="End Time"
            format="12"
            {...form.getInputProps("endedTime")}
            disabled={isFormActive}
          />
        </Grid.Col>
      </Grid>
    </FormCard>
  );
};

export default memo(TimeClockDetail);
